// CustomerGroupsPage — admin view for the customer_groups table.
//
// Operators land here from the sidebar entry, see all groups newest
// first, and can create / edit / delete via the shared ActionDialog
// pattern. Used downstream by Banner targeting in Phase 2.

import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  faPlus,
  faPenToSquare,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Topbar } from 'layouts'
import { ActionDialog, AreYouSure, Button } from 'components'
import { useCustomerGroups } from 'services/queries/use-customer-groups'
import { useDeleteCustomerGroup } from 'services/queries/use-delete-customer-group'
import { CustomerGroupT } from 'types/global'
import CustomerGroupDialog from './customer-groups/customer-group-dialog'

const headerCellSx = {
  py: 1.5,
  px: 2.5,
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const headerTypographySx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
}

const bodyCellSx = {
  py: 1.75,
  px: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const formatCreatedAt = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const CustomerGroupsPage: React.FC = () => {
  const { data: response, isLoading } = useCustomerGroups()
  const deleteMutation = useDeleteCustomerGroup()

  // Per-row "are you sure" state. Keyed by group id so two rapid
  // delete clicks don't interleave.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const groups: CustomerGroupT[] =
    response && response.success ? response.data : []

  const handleConfirmDelete = () => {
    if (!pendingDeleteId) return
    deleteMutation.mutate(pendingDeleteId)
    setPendingDeleteId(null)
  }

  return (
    <>
      <Topbar
        title="Customer groups"
        actions={
          <ActionDialog
            title="New customer group"
            removeActions
            maxWidth="sm"
            fullWidth
            content={(close: () => void) => (
              <CustomerGroupDialog closeFn={close} />
            )}
            render={(open: () => void) => (
              <Button
                onClick={open}
                icon={<FontAwesomeIcon icon={faPlus} />}
                data-cy="new-customer-group"
              >
                New customer group
              </Button>
            )}
          />
        }
      />

      <Box sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}
            >
              All customer groups
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}
            >
              Reusable audience lists used to target announcements and banners.
            </Typography>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 240,
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Loading customer groups...
              </Typography>
            </Box>
          ) : groups.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 280,
                gap: 1.5,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}
              >
                No customer groups yet
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: 'text.secondary',
                  maxWidth: 420,
                }}
              >
                Customer groups let you reuse audience lists across banners and
                announcements. Create your first group to get started.
              </Typography>
              <ActionDialog
                title="New customer group"
                removeActions
                maxWidth="sm"
                fullWidth
                content={(close: () => void) => (
                  <CustomerGroupDialog closeFn={close} />
                )}
                render={(open: () => void) => (
                  <Button
                    onClick={open}
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    data-cy="new-customer-group-empty"
                  >
                    Create your first group
                  </Button>
                )}
              />
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, width: 220 }}>
                      <Typography sx={headerTypographySx}>Name</Typography>
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      <Typography sx={headerTypographySx}>
                        Description
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 120 }}>
                      <Typography sx={headerTypographySx}>Members</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 180 }}>
                      <Typography sx={headerTypographySx}>Created</Typography>
                    </TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 120 }}>
                      <Typography sx={headerTypographySx}>Actions</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow
                      key={group.id}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(84, 189, 203, 0.08)'
                              : 'primary.50',
                        },
                      }}
                    >
                      <TableCell sx={bodyCellSx}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: 'text.primary',
                          }}
                        >
                          {group.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: group.description
                              ? 'text.primary'
                              : 'text.disabled',
                          }}
                        >
                          {group.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography sx={{ fontSize: 14 }}>
                          {group.customer_ids.length}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography
                          sx={{ fontSize: 13, color: 'text.secondary' }}
                        >
                          {formatCreatedAt(group.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <ActionDialog
                            title={`Edit "${group.name}"`}
                            removeActions
                            maxWidth="sm"
                            fullWidth
                            content={(close: () => void) => (
                              <CustomerGroupDialog
                                closeFn={close}
                                group={group}
                              />
                            )}
                            render={(open: () => void) => (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={open}
                                  data-cy={`edit-group-${group.id}`}
                                  aria-label={`Edit ${group.name}`}
                                >
                                  <FontAwesomeIcon
                                    icon={faPenToSquare}
                                    style={{ fontSize: 14 }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          />
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setPendingDeleteId(group.id)}
                              data-cy={`delete-group-${group.id}`}
                              aria-label={`Delete ${group.name}`}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                style={{ fontSize: 14 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <AreYouSure
        open={pendingDeleteId !== null}
        setOpen={(open) => {
          if (!open) setPendingDeleteId(null)
        }}
        onSubmit={handleConfirmDelete}
        title="Delete customer group?"
        content="This permanently removes the group. If any active banner still references it, the delete will be refused and the affected banner IDs will be shown."
      />
    </>
  )
}

export { CustomerGroupsPage }
export default CustomerGroupsPage
